import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../ForBrands/brandMode.css';
import {
  PageHeader, Card, Badge, Btn, SearchBox, Select, EmptyState, ErrorState, Loading, BmModal,
  ConfirmModal, SegmentedControl, Input, TextArea, Checkbox, Avatar,
} from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import TeamCodesPanel, { TeamPlanBlock, programOf } from './TeamCodesPanel';
import TeamRequestsModal, {
  LINK_ALL_URL, REQUESTS_URL, linkAllResultMessage, linkBodyFor,
} from './TeamRequestsModal';
import { teamGamesPath } from './teamPaths';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../constants';

const ADMIN_URL = `${API_BASE_URL}/admin/cache-teams/`;
const UPLOAD_URL = `${API_BASE_URL}/admin/cache-teams/upload/`;

// What the admin reads after a partner team is created from a request.
export const adoptResultMessage = (data = {}) => (
  data.athleteAttached
    ? 'Partner team created. The requester is now on it.'
    : 'Partner team created. The requester was not added to it: they already have a team, or they are not an athlete. Their profile was not changed.'
);

/* ---- social ingest form helpers ----------------------------------------- */

const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', hosts: ['instagram.com'], placeholder: 'https://www.instagram.com/username' },
  { key: 'tiktok', label: 'TikTok', hosts: ['tiktok.com'], placeholder: 'https://www.tiktok.com/@username' },
  { key: 'x', label: 'X (Twitter)', hosts: ['x.com', 'twitter.com'], placeholder: 'https://x.com/username' },
  { key: 'youtube', label: 'YouTube', hosts: ['youtube.com', 'youtu.be'], placeholder: 'https://www.youtube.com/@channel' },
  { key: 'hudl', label: 'Hudl', hosts: ['hudl.com'], placeholder: 'https://www.hudl.com/profile/...' },
];

const emptySocialForm = () => ({
  instagram: '', tiktok: '', x: '', youtube: '', hudl: '', other: '',
});

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
};

const linksToForm = (links) => {
  const form = emptySocialForm();
  const other = [];
  (links || []).forEach((url) => {
    const host = hostOf(url);
    const platform = PLATFORMS.find((p) =>
      p.hosts.some((h) => host === h || host.endsWith(`.${h}`)));
    if (platform && !form[platform.key]) {
      form[platform.key] = url;
    } else {
      other.push(url);
    }
  });
  form.other = other.join('\n');
  return form;
};

const formToLinks = (form) => {
  const links = PLATFORMS.map((p) => form[p.key].trim()).filter(Boolean);
  form.other.split(/[\n,]/).map((u) => u.trim()).filter(Boolean)
    .forEach((u) => { if (!links.includes(u)) links.push(u); });
  return links;
};

/* ---- IRL impact form helpers --------------------------------------------
   Shapes mirror the athlete profile wizard exactly, so claim-time copy is a
   straight transfer: academicAwards/activities/athleticAwards =
   [{name, level}], enrichments = [string], internships = [{companyName,
   role, monthlyValue, monthsWorked, level}], nilDeals = [{title,
   description, value, startDate, endDate, level}], gpa = number. */

const IRL_LEVELS = ['Local', 'Regional', 'National'];
const AWARD_LEVELS = ['Local', 'Regional', 'National', 'Global'];
const INTERNSHIP_LEVELS = AWARD_LEVELS;

const emptyIrlForm = () => ({
  gpa: '', academicAwards: [], athleticAwards: [], activities: [],
  enrichments: '', internships: [], nilDeals: [],
});

const irlToForm = (irl) => ({
  gpa: irl?.gpa || '',
  academicAwards: irl?.academicAwards || [],
  athleticAwards: irl?.athleticAwards || [],
  activities: irl?.activities || [],
  enrichments: (irl?.enrichments || []).join('\n'),
  internships: irl?.internships || [],
  nilDeals: irl?.nilDeals || [],
});

const formToIrl = (form) => ({
  gpa: form.gpa,
  academicAwards: form.academicAwards,
  athleticAwards: form.athleticAwards,
  activities: form.activities,
  enrichments: form.enrichments.split('\n').map((s) => s.trim()).filter(Boolean),
  internships: form.internships,
  nilDeals: form.nilDeals,
});

const irlCount = (irl) => {
  if (!irl) return 0;
  return (irl.gpa ? 1 : 0)
    + ['academicAwards', 'athleticAwards', 'activities', 'enrichments',
      'internships', 'nilDeals']
      .reduce((n, k) => n + ((irl[k] || []).length), 0);
};

/* Linked (claimed) rows are live windows onto the athlete's profile, so the
   roster table shows their current data when the row has none staged. */
const rowSocials = (p) => {
  const live = p.athlete_email ? (p.athlete_profile?.socialMedia || []) : [];
  return live.length ? live : (p.social_media || []);
};

const rowIrlCount = (p) => {
  const staged = irlCount(p.irl_data);
  const ap = p.athlete_email ? p.athlete_profile : null;
  if (!ap) return staged;
  return Math.max(staged, irlCount({
    gpa: ap.gpa,
    academicAwards: ap.academicAwards,
    athleticAwards: ap.athleticAwards,
    activities: ap.activities,
    enrichments: ap.enrichments,
    internships: ap.internships,
    nilDeals: ap.nilDeals,
  }));
};

/* ---- rows scored from entered games --------------------------------------
   A row whose stats_source is "games" takes its stats, raw totals and sport
   score from the games entered on the team's game stats sheet. The sheet
   owns those numbers, so the season-stats editor is closed to the row and
   the player editor never sends them back. */

const fromGames = (p) => p?.stats_source === 'games';

// Linked rows with Cerebro games show the live numbers in GP and PTS.
const liveLeads = (p) => (p?.live_stats?.games_played || 0) > 0;

// The Games badge's tooltip: "From 3 entered games in 2026-27".
export const gamesSourceTitle = (p) => {
  const n = Number(p?.games_count) || 0;
  const games = n ? `${n} entered game${n === 1 ? '' : 's'}` : 'entered games';
  return p?.games_season ? `From ${games} in ${p.games_season}` : `From ${games}`;
};

// "2 entered" under GP when the server counts entered games for the row and
// GP shows other numbers (live Cerebro games, or workbook totals). A row
// scored from its games already says so with the Games badge.
export const enteredGamesNote = (p) => {
  const n = Number(p?.games_entered) || 0;
  if (!n || (fromGames(p) && !liveLeads(p))) return '';
  return `${n} entered`;
};

/* ---- player basics + profile-details form helpers -----------------------
   playerForm edits the roster row's own fields; profileForm stages athlete
   profile basics (profile_data) copied onto the profile at claim time. */

const playerToForm = (p) => ({
  first_name: p?.first_name || '',
  last_name: p?.last_name || '',
  jersey_number: p?.jersey_number || '',
  position: p?.position || '',
  height: p?.height || '',
  weight: p?.weight || '',
});

const profileToForm = (profile) => ({
  gradYear: profile?.gradYear || '',
  homeTown: profile?.homeTown || '',
  state: profile?.state || '',
  dateOfBirth: profile?.dateOfBirth || '',
  coachName: profile?.coachName || '',
});

// The bm positive colour (tailwind.config.js bm.positive) for Badge dots.
const POSITIVE = '#1f9d55';

// A row's remove control inside the IRL list editors.
const RemoveItem = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-bm-tertiary transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
    aria-label="Remove"
  >
    <Icon name="x" size={14} />
  </button>
);

const ListItem = ({ children, level, onRemove }) => (
  <li className="flex items-center justify-between gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-2.5 py-1.5 bm-body-m">
    <span className="truncate text-bm-ink">{children}</span>
    <span className="flex shrink-0 items-center gap-2">
      <span className="bm-label-xs uppercase tracking-[0.06em] text-bm-tertiary">{level}</span>
      <RemoveItem onClick={onRemove} />
    </span>
  </li>
);

/* Name + level list editor (academic achievements, community service) */
const ItemListEditor = ({ label, items, onChange, levels }) => {
  const [name, setName] = useState('');
  const [level, setLevel] = useState(levels[0]);
  const add = () => {
    if (!name.trim()) return;
    onChange([...items, { name: name.trim(), level }]);
    setName('');
  };
  return (
    <div>
      <label className="mb-1 block bm-label-xs text-bm-muted">{label}</label>
      {items.length > 0 && (
        <ul className="mb-2 space-y-1">
          {items.map((it, idx) => (
            <ListItem
              key={`${it.name}-${idx}`}
              level={it.level}
              onRemove={() => onChange(items.filter((_, i) => i !== idx))}
            >
              {it.name}
            </ListItem>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add item..."
          className="min-w-0 flex-1"
        />
        <Select value={level} onChange={(e) => setLevel(e.target.value)} className="!w-auto">
          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Btn variant="secondary" onClick={add} aria-label={`Add ${label}`} className="!w-10 shrink-0 !px-0">
          <Icon name="plus" size={16} />
        </Btn>
      </div>
    </div>
  );
};

/* Internships / work / NIL experience editor */
const InternshipsEditor = ({ items, onChange }) => {
  const empty = { companyName: '', role: '', monthlyValue: '', monthsWorked: '', level: 'Local' };
  const [entry, setEntry] = useState(empty);
  const set = (k) => (e) => setEntry({ ...entry, [k]: e.target.value });
  const add = () => {
    if (!entry.companyName.trim()) return;
    onChange([...items, {
      companyName: entry.companyName.trim(),
      role: entry.role.trim(),
      monthlyValue: parseFloat(entry.monthlyValue) || 0,
      monthsWorked: parseInt(entry.monthsWorked, 10) || 0,
      level: entry.level,
    }]);
    setEntry(empty);
  };
  return (
    <div>
      <label className="mb-1 block bm-label-xs text-bm-muted">Internships / work / NIL experience</label>
      {items.length > 0 && (
        <ul className="mb-2 space-y-1">
          {items.map((it, idx) => (
            <ListItem
              key={`${it.companyName}-${idx}`}
              level={it.level}
              onRemove={() => onChange(items.filter((_, i) => i !== idx))}
            >
              {it.companyName}
              {it.role && <span className="text-bm-tertiary"> · {it.role}</span>}
              {it.monthlyValue > 0 && (
                <span className="text-bm-tertiary"> · ${it.monthlyValue}/mo x {it.monthsWorked}</span>
              )}
            </ListItem>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input type="text" value={entry.companyName} onChange={set('companyName')} placeholder="Company" />
        <Input type="text" value={entry.role} onChange={set('role')} placeholder="Role" />
        <Input type="number" min="0" value={entry.monthlyValue} onChange={set('monthlyValue')} placeholder="$ / month" />
        <Input type="number" min="0" value={entry.monthsWorked} onChange={set('monthsWorked')} placeholder="Months" />
      </div>
      <div className="flex gap-2">
        <Select value={entry.level} onChange={set('level')} className="flex-1">
          {INTERNSHIP_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Btn variant="secondary" onClick={add} className="shrink-0">
          <Icon name="plus" size={14} className="mr-1" /> Add
        </Btn>
      </div>
    </div>
  );
};

/* NIL deals editor (mirrors the profile page's NILInput shape) */
const NilDealsEditor = ({ items, onChange }) => {
  const empty = { title: '', description: '', value: '', startDate: '', endDate: '', level: 'Local' };
  const [entry, setEntry] = useState(empty);
  const set = (k) => (e) => setEntry({ ...entry, [k]: e.target.value });
  const add = () => {
    if (!entry.title.trim()) return;
    onChange([...items, {
      title: entry.title.trim(),
      description: entry.description.trim(),
      value: parseFloat(entry.value) || 0,
      startDate: entry.startDate,
      endDate: entry.endDate,
      level: entry.level,
    }]);
    setEntry(empty);
  };
  return (
    <div>
      <label className="mb-1 block bm-label-xs text-bm-muted">NIL deals</label>
      {items.length > 0 && (
        <ul className="mb-2 space-y-1">
          {items.map((it, idx) => (
            <ListItem
              key={`${it.title}-${idx}`}
              level={it.level}
              onRemove={() => onChange(items.filter((_, i) => i !== idx))}
            >
              {it.title}
              {it.value > 0 && <span className="text-bm-tertiary"> · ${it.value}</span>}
              {it.startDate && <span className="text-bm-tertiary"> · {it.startDate}{it.endDate ? ` → ${it.endDate}` : ''}</span>}
            </ListItem>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input type="text" value={entry.title} onChange={set('title')} placeholder="Deal title" />
        <Input type="number" min="0" value={entry.value} onChange={set('value')} placeholder="$ value" />
        <Input type="date" value={entry.startDate} onChange={set('startDate')} title="Start date" />
        <Input type="date" value={entry.endDate} onChange={set('endDate')} title="End date" />
      </div>
      <Input
        type="text"
        value={entry.description}
        onChange={set('description')}
        placeholder="Description"
        className="mb-2"
      />
      <div className="flex gap-2">
        <Select value={entry.level} onChange={set('level')} className="flex-1">
          {AWARD_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Btn variant="secondary" onClick={add} className="shrink-0">
          <Icon name="plus" size={14} className="mr-1" /> Add
        </Btn>
      </div>
    </div>
  );
};

const titleCase = (s) =>
  (s || '').replace(/\b\w/g, (c) => c.toUpperCase());

const GENDER_LABELS = { M: "Men's / Boys", F: "Women's / Girls" };

const SectionHeader = ({ icon, children }) => (
  <div className="mb-3 flex items-center gap-2">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] bg-bm-subtle text-bm-orange">
      <Icon name={icon} size={15} />
    </span>
    <h3 className="bm-label-l text-bm-ink">{children}</h3>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block bm-label-xs text-bm-muted">{label}</label>
    {children}
  </div>
);

const initialsOf = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

/* ---- team + stats form shapes (from the old Team Management page) ------- */

// Athlete-picked tier → cache_teams level value.
const TIER_TO_LEVEL = { hs: 'HS', college: 'College', pro: 'Pro' };

// The sports CACHE scores, and the age groups of a grassroots squad.
export const TEAM_SPORTS = [{ value: 'basketball', label: 'Basketball' }, { value: 'football', label: 'Football' }];
export const AGE_GROUPS = ['17U', '16U', '15U', '14U', '13U', '12U', '11U', '10U'];

const emptyTeamForm = {
  name: '',
  organization: '',
  partner_program: '',
  league_label: '',
  sport: 'basketball',
  gender: 'M',
  age_group: '',
  level: 'HS',
  city: '',
  state: '',
  season: '',
  notes: '',
  active: true,
  cerebro_program_id: '',
  cerebro_program_name: '',
  cerebro_program_ids: [],
  cerebro_program_names: [],
};

// The Cerebro programs a team merges, as [{id, name}] (the list, else the
// single field older teams carry).
export const programsOf = (form = {}) => {
  const ids = Array.isArray(form.cerebro_program_ids) && form.cerebro_program_ids.length
    ? form.cerebro_program_ids
    : (form.cerebro_program_id ? [form.cerebro_program_id] : []);
  const names = Array.isArray(form.cerebro_program_names) ? form.cerebro_program_names : [];
  return ids.map((id, i) => ({ id, name: names[i] || (i === 0 ? form.cerebro_program_name : '') || id }));
};

// The form with its programs set: the list, and the first one in the single
// fields older readers use.
export const withPrograms = (form, programs) => ({
  ...form,
  cerebro_program_ids: programs.map((p) => p.id),
  cerebro_program_names: programs.map((p) => p.name || ''),
  cerebro_program_id: programs[0]?.id || '',
  cerebro_program_name: programs[0]?.name || '',
});

// A team's form values (the edit form, and a full save such as Confirm team).
export const teamToForm = (t = {}) => withPrograms({
  ...emptyTeamForm,
  name: t.name || '',
  organization: t.organization || '',
  partner_program: t.partner_program || '',
  league_label: t.league_label || '',
  sport: t.sport || 'basketball',
  gender: t.gender || 'M',
  age_group: t.age_group || '',
  level: t.level || 'HS',
  city: t.city || '',
  state: t.state || '',
  season: t.season || '',
  notes: t.notes || '',
  active: !!t.active,
}, programsOf(t));

// A partner team as the team plan block (TeamCodesPanel.TeamPlanBlock) and
// admin/team-plans/ read it: its id, source 'cache_teams' and its CACHE
// league. null for a team with no league key.
export const partnerPlanRequest = (t) => (t?.team_id && t?.league_key
  ? { teamId: String(t.team_id), source: 'cache_teams', league: t.league_key, teamName: t.name || '' }
  : null);

// Where a possible duplicate lives, in staff words (an admin page).
export const DUPLICATE_SOURCE_LABEL = {
  cache_teams: 'CACHE Partner',
  cerebro: 'Cerebro',
  sportsradar: 'NCAA D-I',
  team_code: 'Team code',
  team_request: 'Pending request',
};

const emptyPlayerRaw = {
  '#': '', Name: '', GP: '', 'FGM-FGA': '', 'FG%': '',
  '3PM-3PA': '', '3FG%': '', 'FTM-FTA': '', 'FT%': '',
  REB: '', PF: '', PTS: '', AST: '', TO: '', BLK: '', STL: '',
};

/* Merge with a Cerebro program: search Cerebro by school or club name and
   pin the program on the team. Pickers then show this roster in place of
   the Cerebro one, and matched players get live stats. Picking a program
   asks first, because the merge redirects the whole program. */
export const MERGE_WARNING = 'Merging sends every picker, roster and roster claim for this Cerebro program to this partner team. '
  + 'All of its squads (for example 17U, 16U and 15U) show as this one team, and athletes already on the Cerebro roster '
  + 'can lose their roster match if this roster is empty or incomplete. Merge only when this partner team is the same program '
  + 'and its roster is complete.';

const CerebroProgramField = ({ form, setForm }) => {
  const token = useSelector((state) => state.auth.token);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pendingHit, setPendingHit] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    let cancelled = false;
    setBusy(true);
    const timer = setTimeout(() => {
      axios.post(
        `${API_BASE_URL}/cerebro/live/teams/`,
        { cerebroSports: ['cbx_any'], teamQuery: q.trim(), gender: form.gender || 'M' },
        { headers: { Authorization: `Bearer ${token}` } },
      )
        .then((res) => { if (!cancelled) setHits(res.data?.teams || []); })
        .catch(() => { if (!cancelled) setHits([]); })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, form.gender, token]);
  // A team may merge several programs: Cerebro has a program per season, and
  // a school can have two entries.
  const programs = programsOf(form);
  const merged = new Set(programs.map((p) => p.id));
  return (
    <Field label="Cerebro programs (merge)">
      {programs.length > 0 && (
        <div className="mb-2 flex flex-col gap-2">
          {programs.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2">
              <span className="inline-flex min-w-0 items-center gap-1.5 truncate bm-label-l text-bm-n700">
                <Icon name="activity" size={13} /> {p.name || p.id}
              </span>
              <Btn
                size="sm"
                variant="secondary"
                onClick={() => setForm(withPrograms(form, programs.filter((x) => x.id !== p.id)))}
                className="shrink-0"
              >
                Unlink
              </Btn>
            </div>
          ))}
        </div>
      )}
      {pendingHit ? (
        <div role="alert" className="rounded-[8px] border border-bm-border border-l-[3px] border-l-bm-critical bg-bm-subtle px-4 py-3">
          <div className="flex items-center gap-1.5 bm-label-l text-bm-critical">
            <Icon name="alert-triangle" size={14} /> Merge with {pendingHit.name}?
          </div>
          <p className="mt-1 bm-body-m text-bm-n700">{MERGE_WARNING}</p>
          <div className="mt-3 flex gap-2">
            <Btn size="sm" variant="secondary" onClick={() => setPendingHit(null)}>Cancel</Btn>
            <Btn
              size="sm"
              className="!bg-bm-critical hover:!bg-[#c41d14]"
              onClick={() => {
                setForm(withPrograms(form, [...programs, { id: pendingHit.id, name: pendingHit.name }]));
                setPendingHit(null);
                setQ('');
                setHits([]);
              }}
            >
              Merge anyway
            </Btn>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Cerebro by school or club name"
          />
          {q.trim().length >= 2 && (busy || hits.length > 0) && (
            <ul className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-[8px] border border-bm-border bg-white bm-body-m shadow-xl">
              {busy && hits.length === 0 && <li className="px-3 py-2 text-bm-tertiary">Searching Cerebro...</li>}
              {hits.filter((h) => !merged.has(h.id)).slice(0, 12).map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => setPendingHit({ id: h.id, name: h.name })}
                    className="w-full px-3 py-2 text-left hover:bg-bm-subtle"
                  >
                    <span className="bm-label-l text-bm-ink">{h.name}</span>
                    {h.latestEvent?.startDate && (
                      <span className="ml-2 bm-body-s text-bm-tertiary">last game {String(h.latestEvent.startDate).slice(0, 10)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="mt-1 bm-body-s text-bm-muted">
        Merges Cerebro programs into this team (one per season, or a school&apos;s second entry): pickers show this roster instead of the Cerebro one, players matched to Cerebro get live stats, and athletes on those programs move to this team.
      </div>
    </Field>
  );
};

const TeamModal = ({ form, setForm, editing, fromRequest = false, onSubmit, onClose }) => (
  <BmModal
    title={editing ? 'Edit team' : fromRequest ? 'Create partner team' : 'New team'}
    onClose={onClose}
    width={720}
    footer={(
      <>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn type="submit" form="team-form">{editing ? 'Update team' : fromRequest ? 'Create partner team' : 'Create team'}</Btn>
      </>
    )}
  >
      <form id="team-form" onSubmit={onSubmit} className="space-y-4">
        {fromRequest && (
          <div role="note" className="rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2.5 bm-body-m text-bm-n700">
            This creates a new CACHE partner team from the request. If the team is already in the API, close this and use Find in API, then Link requester.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Team name *">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="All In Elite 17U"
              required
            />
          </Field>
          <Field label="Organization">
            <Input
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              placeholder="The team name when left blank"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Partner program">
            <Input
              value={form.partner_program}
              onChange={(e) => setForm({ ...form, partner_program: e.target.value })}
              placeholder="Under Armour Next"
            />
            <div className="mt-1 bm-body-s text-bm-muted">
              Shown as the league name in CacheShare onboarding (with the CACHE logo).
            </div>
          </Field>
          <Field label="League label">
            <Input
              value={form.league_label}
              onChange={(e) => setForm({ ...form, league_label: e.target.value })}
              placeholder="Boys Under Armour Association (UAA), Basketball"
            />
          </Field>
        </div>
        <CerebroProgramField form={form} setForm={setForm} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Sport">
            <Select
              value={form.sport}
              onChange={(e) => setForm({ ...form, sport: e.target.value })}
            >
              {TEAM_SPORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </Field>
          <Field label="Gender">
            <Select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option value="M">Men/Boys</option>
              <option value="F">Women/Girls</option>
            </Select>
          </Field>
          <Field label="Level">
            <Select
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value, age_group: e.target.value === 'HS' ? form.age_group : '' })}
            >
              <option value="College">College</option>
              <option value="Pro">Pro</option>
              <option value="HS">HS / Grassroots</option>
            </Select>
          </Field>
          <Field label="Age group">
            <Select
              value={form.age_group}
              onChange={(e) => setForm({ ...form, age_group: e.target.value })}
              disabled={form.level !== 'HS'}
              aria-label="Age group"
            >
              <option value="">None</option>
              {[...new Set([...AGE_GROUPS, ...(form.age_group ? [form.age_group] : [])])].map((age) => (
                <option key={age} value={age}>{age}</option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City">
            <Input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </Field>
          <Field label="State">
            <Input
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
            />
          </Field>
          <Field label="Season">
            <Input
              value={form.season}
              onChange={(e) => setForm({ ...form, season: e.target.value })}
              placeholder="2026"
            />
          </Field>
        </div>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
          />
        </Field>
        <Checkbox
          checked={!!form.active}
          onChange={(v) => setForm({ ...form, active: v })}
          label="Active (visible in CacheShare onboarding and coach assignment)"
        />
      </form>
  </BmModal>
);

/* Teams that may already be the one being created (the server's existing
   team check). Staff only, so the source names the data provider. */
export const DuplicateTeamsModal = ({
  dupes, fromRequest = false, onBack, onCreateAnyway, onOpenTeam, onOpenRequests, onLinkRequester,
}) => {
  const rows = dupes?.rows || [];
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };
  return (
    <BmModal
      title="Teams like this already exist"
      onClose={() => !busy && onBack?.()}
      width={720}
      footer={(
        <>
          <Btn variant="secondary" onClick={onBack} disabled={busy}>Back to the form</Btn>
          <Btn onClick={() => run(onCreateAnyway)} disabled={busy}>{busy ? 'Working...' : 'Create anyway'}</Btn>
        </>
      )}
    >
      <p className="mb-3 bm-body-m text-bm-n700">
        Open one of these instead of creating a second team for the same players, or create the team anyway.
      </p>
      {(dupes?.skipped || []).length > 0 && (
        <p className="mb-3 bm-body-s text-bm-critical">
          {`Not checked right now: ${dupes.skipped.map((s) => DUPLICATE_SOURCE_LABEL[s] || s).join(', ')}. Try again in a minute before creating a team.`}
        </p>
      )}
      <ul className="divide-y divide-bm-border rounded-[8px] border border-bm-border" data-testid="duplicate-teams">
        {rows.map((row) => {
          const where = [row.leagueLabel, row.ageGroup, row.state].filter(Boolean).join(' · ');
          const linkable = fromRequest && row.teamId && ['cache_teams', 'cerebro', 'sportsradar'].includes(row.source);
          return (
            <li key={`${row.source}-${row.teamId || row.name}-${row.league || ''}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="bm-label-l text-bm-ink">{row.displayName || row.name}</span>
                  <Badge tone="soft">{DUPLICATE_SOURCE_LABEL[row.source] || row.source}</Badge>
                  {row.exact && <Badge tone="outline">Same name</Badge>}
                  {row.provisional && <Badge tone="attention">Provisional</Badge>}
                  {row.requestCount > 0 && <Badge tone="outline">{`${row.requestCount} pending`}</Badge>}
                </div>
                {where && <div className="truncate bm-body-s text-bm-tertiary">{where}</div>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {row.source === 'cache_teams' && (
                  <Btn size="sm" variant="secondary" onClick={() => onOpenTeam?.(row)} disabled={busy}>Open team</Btn>
                )}
                {row.source === 'team_request' && (
                  <Btn size="sm" variant="secondary" onClick={() => onOpenRequests?.()} disabled={busy}>Open requests</Btn>
                )}
                {linkable && (
                  <Btn size="sm" onClick={() => run(() => onLinkRequester?.(row))} disabled={busy}>Link requester</Btn>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </BmModal>
  );
};

/* Season-stats editor: name/position/jersey plus the raw counting totals the
   sport score is computed from. Profile-ish data lives in the row editor. */
const StatsModal = ({ form, setForm, editing, onSubmit, onClose }) => {
  const setRaw = (key, value) => setForm({ ...form, raw: { ...form.raw, [key]: value } });
  const rawFields = ['GP', 'PTS', 'REB', 'AST', 'STL', 'BLK', 'TO', 'PF'];
  const splitFields = ['FGM-FGA', '3PM-3PA', 'FTM-FTA'];
  const pctFields = ['FG%', '3FG%', 'FT%'];
  return (
    <BmModal
      title={editing ? 'Edit season stats' : 'New player'}
      onClose={onClose}
      width={720}
      footer={(
        <>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" form="stats-form">{editing ? 'Update stats' : 'Add player'}</Btn>
        </>
      )}
    >
        <form id="stats-form" onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Full name *">
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value, raw: { ...form.raw, Name: e.target.value } })}
                required
              />
            </Field>
            <Field label="Position">
              <Input
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                placeholder="PG / SG / SF / PF / C"
              />
            </Field>
            <Field label="Jersey #">
              <Input
                value={form.jersey_number}
                onChange={(e) => setForm({ ...form, jersey_number: e.target.value, raw: { ...form.raw, '#': e.target.value } })}
              />
            </Field>
          </div>

          <div>
            <div className="bm-label-xs uppercase tracking-[0.06em] text-bm-tertiary mb-2">Counting stats (season totals)</div>
            <div className="grid grid-cols-4 gap-3">
              {rawFields.map((k) => (
                <Field key={k} label={k}>
                  <Input
                    type="number"
                    value={form.raw[k] ?? ''}
                    onChange={(e) => setRaw(k, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <div className="bm-label-xs uppercase tracking-[0.06em] text-bm-tertiary mb-2">Made-attempted</div>
            <div className="grid grid-cols-3 gap-3">
              {splitFields.map((k) => (
                <Field key={k} label={k}>
                  <Input
                    value={form.raw[k] ?? ''}
                    onChange={(e) => setRaw(k, e.target.value)}
                    placeholder="17-56"
                  />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <div className="bm-label-xs uppercase tracking-[0.06em] text-bm-tertiary mb-2">Percentages</div>
            <div className="grid grid-cols-3 gap-3">
              {pctFields.map((k) => (
                <Field key={k} label={k}>
                  <Input
                    value={form.raw[k] ?? ''}
                    onChange={(e) => setRaw(k, e.target.value)}
                    placeholder="63%"
                  />
                </Field>
              ))}
            </div>
          </div>

        </form>
    </BmModal>
  );
};

/* ---- page ---------------------------------------------------------------- */

const TeamManagementPage = () => {
  const token = useSelector((state) => state.auth.token);
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  // 'rosters' = CACHE Partner teams and their rosters; 'codes' = team codes
  // for every team CacheShare can list.
  const [view, setView] = useState('rosters');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [sport, setSport] = useState('');
  const [gender, setGender] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [error, setError] = useState('');

  // Team requests (athletes/coaches who couldn't find their team in onboarding)
  const [requests, setRequests] = useState([]);
  const [adoptingRequestId, setAdoptingRequestId] = useState(null);
  // A partner team created for several requests of one team links them all.
  const [adoptingRequests, setAdoptingRequests] = useState([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);

  // Team create/edit modal
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState(emptyTeamForm);
  const [editingTeamId, setEditingTeamId] = useState(null);
  // Teams that may already exist for the one being created: {rows, payload}.
  const [dupes, setDupes] = useState(null);
  const [onlyProvisional, setOnlyProvisional] = useState(false);
  // Pending requests for the open team ("Link all N requesters").
  const [pendingRequesters, setPendingRequesters] = useState(0);
  const [confirmLinkAll, setConfirmLinkAll] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);

  // Season stats / new player modal
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsForm, setStatsForm] = useState({ full_name: '', position: '', jersey_number: '', raw: { ...emptyPlayerRaw } });
  const [editingPlayerId, setEditingPlayerId] = useState(null);

  // Bulk import
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef();

  // Player data (profile-style) editor
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [socialForm, setSocialForm] = useState(emptySocialForm());
  const [irlForm, setIrlForm] = useState(emptyIrlForm());
  const [playerForm, setPlayerForm] = useState(playerToForm(null));
  const [profileForm, setProfileForm] = useState(profileToForm(null));
  const [showModal, setShowModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Delete team / remove player wait on a ConfirmModal:
  // { kind: 'team', team } or { kind: 'player', player }.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);
  // Cerebro merge: per-player match results (candidates for unmatched rows)
  // and the manual Cerebro player search in the player editor.
  const [matchInfo, setMatchInfo] = useState({});
  const [matching, setMatching] = useState(false);
  const [cerebroQuery, setCerebroQuery] = useState('');
  const [cerebroHits, setCerebroHits] = useState([]);
  const [cerebroSearching, setCerebroSearching] = useState(false);
  const [cerebroLinking, setCerebroLinking] = useState(false);

  // The open team rides in the address (?team=<team_id>): the game stats
  // sheet's Back link opens the page on it, and a reload or the browser's
  // back button comes back to it. The team named when the page loads opens
  // once the list arrives.
  const [searchParams, setSearchParams] = useSearchParams();
  const teamToRestore = useRef(searchParams.get('team') || '');

  const applyPlayerUpdate = (updated) => {
    setPlayers((prev) => prev.map((p) => (p.player_id === updated.player_id ? { ...p, ...updated } : p)));
    setSelectedPlayer((prev) => (prev && prev.player_id === updated.player_id ? { ...prev, ...updated } : prev));
  };

  const matchRosterToCerebro = async () => {
    if (!selectedTeam) return;
    setMatching(true);
    try {
      const res = await axios.post(`${ADMIN_URL}${selectedTeam.team_id}/cerebro/match/`, { apply: true }, authHeaders);
      setPlayers(res.data.players || []);
      if (res.data.team) setSelectedTeam(res.data.team);
      const info = {};
      (res.data.matches || []).forEach((m) => { info[m.player_id] = m; });
      setMatchInfo(info);
      const total = (res.data.matches || []).length;
      toast.success(`${res.data.matched || 0} of ${total} players linked to Cerebro players.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Cerebro match failed.');
    } finally {
      setMatching(false);
    }
  };

  const linkCerebroPlayer = async (player, cerebroPlayerId, cerebroName) => {
    if (!selectedTeam || !player) return;
    setCerebroLinking(true);
    try {
      const body = cerebroPlayerId ? { cerebroPlayerId, cerebroName: cerebroName || '' } : { unlink: true };
      const res = await axios.post(
        `${ADMIN_URL}${selectedTeam.team_id}/players/${player.player_id}/cerebro/`, body, authHeaders,
      );
      if (res.data.player) applyPlayerUpdate(res.data.player);
      setCerebroQuery('');
      setCerebroHits([]);
      toast.success(cerebroPlayerId ? `Linked to ${cerebroName || 'the Cerebro player'}.` : 'Cerebro link removed.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update the Cerebro link.');
    } finally {
      setCerebroLinking(false);
    }
  };

  useEffect(() => {
    if (cerebroQuery.trim().length < 2) { setCerebroHits([]); return undefined; }
    let cancelled = false;
    setCerebroSearching(true);
    const timer = setTimeout(() => {
      axios.get(`${API_BASE_URL}/cerebro/live/athletes/`, {
        ...authHeaders,
        params: { name: cerebroQuery.trim(), gender: selectedTeam?.gender || undefined, limit: 12 },
      })
        .then((res) => { if (!cancelled) setCerebroHits(res.data?.results || []); })
        .catch(() => { if (!cancelled) setCerebroHits([]); })
        .finally(() => { if (!cancelled) setCerebroSearching(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [cerebroQuery, selectedTeam?.gender, authHeaders]);

  const fetchTeams = () => {
    setLoadingTeams(true);
    axios.get(ADMIN_URL, authHeaders)
      .then((res) => setTeams(res.data.teams || []))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load teams.'))
      .finally(() => setLoadingTeams(false));
  };

  const fetchRequests = () => {
    axios.get(REQUESTS_URL, { ...authHeaders, params: { status: 'pending' } })
      .then((res) => setRequests(res.data.requests || []))
      .catch(() => setRequests([]));
  };

  useEffect(() => {
    fetchTeams();
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sports = useMemo(
    () => [...new Set(teams.map((t) => t.sport).filter(Boolean))].sort(),
    [teams],
  );
  const genders = useMemo(
    () => [...new Set(teams
      .filter((t) => !sport || t.sport === sport)
      .map((t) => t.gender).filter(Boolean))],
    [teams, sport],
  );

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    return teams
      .filter((t) => (!sport || t.sport === sport)
        && (!gender || t.gender === gender)
        && (!onlyProvisional || t.provisional)
        && (!q || `${t.name} ${t.organization || ''} ${t.partner_program || ''} ${t.city || ''} ${t.state || ''} ${t.age_group || ''}`
          .toLowerCase().includes(q)))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [teams, sport, gender, teamSearch, onlyProvisional]);
  const provisionalCount = useMemo(() => teams.filter((t) => t.provisional).length, [teams]);

  /* ---- teams ---- */

  const openTeam = (team) => {
    setSelectedTeam(team);
    setPlayers([]);
    setSelectedPlayer(null);
    setPendingRequesters(0);
    setLoadingRoster(true);
    axios.get(`${ADMIN_URL}${team.team_id}/`, authHeaders)
      .then((res) => {
        setSelectedTeam(res.data.team || team);
        setPlayers(res.data.players || []);
        setPendingRequesters(res.data.pendingRequesters || 0);
      })
      .catch(() => setError('Failed to load the roster.'))
      .finally(() => setLoadingRoster(false));
  };

  // Replaces the address rather than adding to the history, so the back
  // button leaves the page instead of stepping through earlier picks.
  const rememberTeam = (teamId) => setSearchParams(teamId ? { team: teamId } : {}, { replace: true });

  const selectTeam = (team) => {
    openTeam(team);
    rememberTeam(team.team_id);
  };

  // Once only: later list refreshes (after a save) keep the admin's pick. A
  // team that is gone leaves the address.
  useEffect(() => {
    const teamId = teamToRestore.current;
    if (!teamId || !teams.length) return;
    teamToRestore.current = '';
    const team = teams.find((t) => t.team_id === teamId);
    if (team) openTeam(team);
    else rememberTeam('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  const openCreateTeam = () => {
    setEditingTeamId(null);
    setAdoptingRequestId(null);
    setAdoptingRequests([]);
    setTeamForm({ ...emptyTeamForm });
    setShowTeamModal(true);
  };

  const openEditTeam = (t) => {
    setEditingTeamId(t.team_id);
    setAdoptingRequestId(null);
    setAdoptingRequests([]);
    setTeamForm(teamToForm(t));
    setShowTeamModal(true);
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    setEditingTeamId(null);
    setAdoptingRequestId(null);
    setAdoptingRequests([]);
    setDupes(null);
  };

  // The team payload as saved: an organization left blank is the team name.
  // A team made for requests names them, so the server's duplicate check
  // does not list them as a team that exists.
  const teamPayload = () => ({
    ...teamForm,
    organization: (teamForm.organization || '').trim() || (teamForm.name || '').trim(),
    ...(adoptingRequests.length ? { requestIds: adoptingRequests.map((r) => r.request_id) } : {}),
  });

  const saveTeam = async (e) => {
    e.preventDefault();
    setError('');
    if (editingTeamId) {
      try {
        const res = await axios.put(`${ADMIN_URL}${editingTeamId}/`, teamPayload(), authHeaders);
        toast.success('Team updated.');
        if (selectedTeam?.team_id === editingTeamId) {
          setSelectedTeam(res.data.team || selectedTeam);
        }
        closeTeamModal();
        fetchTeams();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Save failed.');
      }
      return;
    }
    await createTeam(teamPayload());
  };

  // Create checks every team that exists first: a likely duplicate answers
  // 409 possible_duplicates and the admin decides (createTeam with
  // confirmedNoMatch from the duplicates dialog).
  const createTeam = async (payload) => {
    let res;
    try {
      res = await axios.post(ADMIN_URL, payload, authHeaders);
    } catch (err) {
      const data = err?.response?.data || {};
      if (err?.response?.status === 409 && data.error === 'possible_duplicates') {
        setDupes({ rows: data.possibleDuplicates || [], skipped: data.skipped || [], payload });
        return;
      }
      toast.error(data.error || 'Save failed.');
      return;
    }
    setDupes(null);
    const team = res.data?.team || {};
    if (adoptingRequests.length > 1 && team.team_id) {
      // Created for several requests of one team: every requester is linked
      // to it (as one "Link requester" each).
      try {
        const linked = await axios.post(`${ADMIN_URL}${team.team_id}/link-requesters/`,
          { requestIds: adoptingRequests.map((r) => r.request_id) }, authHeaders);
        const msg = linkAllResultMessage(linked?.data || {}, team.name || 'the team');
        if (linked?.data?.linkedCount) toast.success(msg);
        else toast.info(msg);
      } catch (linkErr) {
        toast.error('Partner team created, but the requesters could not be linked. Link them from Requests.');
      }
      fetchRequests();
    } else if (adoptingRequestId) {
      // Created from a team request: resolve it so it leaves the pending
      // queue and links to the new partner team. The requester joins it
      // only when they have no team yet; say so either way.
      try {
        const resolved = await axios.post(
          `${REQUESTS_URL}${adoptingRequestId}/resolve/`,
          { action: 'adopt', team_id: team.team_id || '' },
          authHeaders,
        );
        const data = resolved?.data || {};
        if (data.athleteAttached) toast.success(adoptResultMessage(data));
        else toast.info(adoptResultMessage(data));
      } catch (resolveErr) {
        // The team still exists; only the request was not closed.
        toast.error(resolveErr?.response?.status === 409
          ? 'Partner team created. The request was already handled, so nothing else changed.'
          : 'Partner team created, but the request could not be closed. Dismiss it from Requests.');
      }
      fetchRequests();
    } else if (res.data?.created === false) {
      toast.info('A team with the same organization, age group, gender, sport and season existed, so it was updated.');
    } else {
      toast.success('Team created.');
    }
    closeTeamModal();
    fetchTeams();
  };

  // "Link requester" from the duplicates dialog: the request (or every
  // request of the group) goes to a team that exists instead of a new
  // partner team.
  const linkRequestToExisting = async (row) => {
    if (!adoptingRequestId) return;
    try {
      if (adoptingRequests.length > 1) {
        const res = await axios.post(LINK_ALL_URL,
          { ...linkBodyFor(row), requestIds: adoptingRequests.map((r) => r.request_id) }, authHeaders);
        const msg = linkAllResultMessage(res.data || {}, row.name || 'the team');
        if (res.data?.linkedCount) toast.success(msg);
        else toast.info(msg);
      } else {
        const resolved = await axios.post(`${REQUESTS_URL}${adoptingRequestId}/resolve/`,
          linkBodyFor(row), authHeaders);
        const data = resolved?.data || {};
        toast.success(data.alreadyOnTeam
          ? `The requester is already on ${row.name}. The request is closed.`
          : `The requester is linked to ${row.name}. The request is closed.`);
      }
      closeTeamModal();
      fetchRequests();
    } catch (err) {
      const data = err?.response?.data || {};
      toast.error(data.message || 'Could not link the requester.');
    }
  };

  // Mark a provisional team (made for a team an athlete added) as reviewed.
  // A full save, as the edit form makes, so nothing else changes.
  const confirmTeam = async (t) => {
    setTeamBusy(true);
    try {
      const res = await axios.put(`${ADMIN_URL}${t.team_id}/`, { ...teamToForm(t), provisional: false }, authHeaders);
      setSelectedTeam(res.data.team || { ...t, provisional: false });
      toast.success(`${t.name} is a confirmed team now.`);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not confirm the team.');
    } finally {
      setTeamBusy(false);
    }
  };

  const linkAllRequesters = async () => {
    if (!selectedTeam) return;
    setTeamBusy(true);
    try {
      const res = await axios.post(`${ADMIN_URL}${selectedTeam.team_id}/link-requesters/`, {}, authHeaders);
      const msg = linkAllResultMessage(res.data || {}, selectedTeam.name || 'the team');
      if (res.data?.linkedCount) toast.success(msg);
      else toast.info(msg);
      setConfirmLinkAll(false);
      fetchRequests();
      openTeam(selectedTeam);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not link the requesters.');
    } finally {
      setTeamBusy(false);
    }
  };

  // Runs once the admin confirms in the ConfirmModal.
  const deleteTeam = async (t) => {
    try {
      await axios.delete(`${ADMIN_URL}${t.team_id}/`, authHeaders);
      toast.success(`Deleted ${t.name}.`);
      if (selectedTeam?.team_id === t.team_id) {
        setSelectedTeam(null);
        setPlayers([]);
        rememberTeam('');
      }
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  /* ---- team requests ---- */

  // `group`: every request of one team (the Requests list's grouped row);
  // the new team links them all.
  const adoptRequest = (req, group) => {
    setShowRequestsModal(false);
    setEditingTeamId(null);
    setAdoptingRequestId(req.request_id);
    setAdoptingRequests(Array.isArray(group) ? group : [req]);
    setTeamForm({
      ...emptyTeamForm,
      name: req.teamName || '',
      organization: req.teamName || '',
      gender: req.gender === 'F' ? 'F' : 'M',
      level: TIER_TO_LEVEL[req.tier] || 'HS',
      sport: req.sport && /fb$|football/i.test(req.sport) ? 'football' : 'basketball',
      city: req.city || '',
      state: req.state || '',
    });
    setShowTeamModal(true);
  };

  /* ---- bulk import ---- */

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(UPLOAD_URL, form, {
        headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' },
      });
      const imported = res.data?.imported || [];
      const total = imported.reduce((a, t) => a + (t.players || 0), 0);
      toast.success(`Imported ${imported.length} team(s), ${total} player(s).`);
      fetchTeams();
      if (selectedTeam) openTeam(selectedTeam);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  /* ---- season stats / add + delete player ---- */

  const openCreatePlayer = () => {
    setEditingPlayerId(null);
    setStatsForm({ full_name: '', position: '', jersey_number: '', raw: { ...emptyPlayerRaw } });
    setShowStatsModal(true);
  };

  const openEditStats = (p) => {
    setEditingPlayerId(p.player_id);
    setStatsForm({
      full_name: p.full_name || '',
      position: p.position || '',
      jersey_number: p.jersey_number || '',
      raw: { ...emptyPlayerRaw, ...(p.raw || {}), Name: p.full_name || '' },
    });
    setShowStatsModal(true);
  };

  const saveStats = async (e) => {
    e.preventDefault();
    if (!selectedTeam) return;
    // Keep the row's non-stats fields: the upsert rewrites the whole doc, so
    // omitting height/weight here would blank what the row editor staged.
    const base = editingPlayerId
      ? players.find((p) => p.player_id === editingPlayerId)
      : null;
    const payload = {
      full_name: statsForm.full_name,
      position: statsForm.position,
      jersey_number: statsForm.jersey_number,
      height: base?.height || '',
      weight: base?.weight || '',
      status: base?.status || '',
      active: base ? base.active : true,
      raw: statsForm.raw,
    };
    try {
      if (editingPlayerId) {
        await axios.put(
          `${ADMIN_URL}${selectedTeam.team_id}/players/${editingPlayerId}/`,
          payload,
          authHeaders,
        );
      } else {
        await axios.post(
          `${ADMIN_URL}${selectedTeam.team_id}/players/`,
          payload,
          authHeaders,
        );
      }
      toast.success(`Saved ${statsForm.full_name}'s stats.`);
      setShowStatsModal(false);
      setEditingPlayerId(null);
      openTeam(selectedTeam);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed.');
    }
  };

  // Runs once the admin confirms in the ConfirmModal.
  const deletePlayer = async (p) => {
    try {
      await axios.delete(
        `${ADMIN_URL}${selectedTeam.team_id}/players/${p.player_id}/`,
        authHeaders,
      );
      setPlayers((prev) => prev.filter((x) => x.player_id !== p.player_id));
      toast.success(`Removed ${p.full_name}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  /* ---- profile-style player data editor ---- */

  const pickPlayer = (player) => {
    setSelectedPlayer(player);
    // Claimed rows are a live window onto the athlete's profile: their
    // current values win over whatever was staged on the roster row.
    const ap = player.athlete_email ? (player.athlete_profile || {}) : null;
    const pick = (apVal, stagedVal) => {
      const has = Array.isArray(apVal)
        ? apVal.length > 0
        : apVal !== undefined && apVal !== null && apVal !== '';
      return has ? apVal : stagedVal;
    };
    const stagedIrl = player.irl_data || {};
    const stagedProfile = player.profile_data || {};
    const baseForm = playerToForm(player);
    if (ap) {
      setSocialForm(linksToForm(pick(ap.socialMedia, player.social_media)));
      setIrlForm(irlToForm({
        gpa: pick(ap.gpa, stagedIrl.gpa),
        academicAwards: pick(ap.academicAwards, stagedIrl.academicAwards),
        athleticAwards: pick(ap.athleticAwards, stagedIrl.athleticAwards),
        activities: pick(ap.activities, stagedIrl.activities),
        enrichments: pick(ap.enrichments, stagedIrl.enrichments),
        internships: pick(ap.internships, stagedIrl.internships),
        nilDeals: pick(ap.nilDeals, stagedIrl.nilDeals),
      }));
      setPlayerForm({
        ...baseForm,
        jersey_number: String(pick(ap.jerseyNumber, baseForm.jersey_number) ?? ''),
        height: String(pick(ap.height, baseForm.height) ?? ''),
        weight: String(pick(ap.weight, baseForm.weight) ?? ''),
      });
      setProfileForm(profileToForm({
        gradYear: pick(ap.gradYear, stagedProfile.gradYear),
        homeTown: pick(ap.homeTown, stagedProfile.homeTown),
        state: pick(ap.state, stagedProfile.state),
        dateOfBirth: String(pick(ap.dateOfBirth, stagedProfile.dateOfBirth) || '').slice(0, 10),
        coachName: pick(ap.coachName, stagedProfile.coachName),
      }));
    } else {
      setSocialForm(linksToForm(player.social_media));
      setIrlForm(irlToForm(stagedIrl));
      setPlayerForm(baseForm);
      setProfileForm(profileToForm(stagedProfile));
    }
    setConfirmOpen(false);
    setLinkEmail('');
    setShowModal(true);
  };

  // Link/unlink a roster row to an athlete account. Independent of the
  // athlete's primary team, so one athlete can sit on several rosters
  // (school team + travel team).
  const linkAthlete = async (unlink = false) => {
    if (!selectedTeam || !selectedPlayer) return;
    if (!unlink && !linkEmail.trim()) return;
    setLinking(true);
    try {
      const res = await axios.post(
        `${ADMIN_URL}${selectedTeam.team_id}/players/${selectedPlayer.player_id}/link/`,
        unlink ? { unlink: true } : { email: linkEmail.trim() },
        authHeaders,
      );
      const updated = res.data.player;
      setPlayers((prev) => prev.map((p) => (p.player_id === updated.player_id ? updated : p)));
      toast.success(unlink
        ? 'Unlinked the athlete account from this roster spot.'
        : `Linked to ${updated.athlete_email}.`);
      pickPlayer(updated);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update the link.');
    }
    setLinking(false);
  };

  const closeModal = () => {
    setConfirmOpen(false);
    setShowModal(false);
    setSelectedPlayer(null);
  };

  const runPendingDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === 'team') await deleteTeam(pendingDelete.team);
      else await deletePlayer(pendingDelete.player);
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleSave = async () => {
    if (!selectedTeam || !selectedPlayer) return;
    setSaving(true);
    setError('');
    try {
      // Send the full player payload: the upsert endpoint rewrites the whole
      // doc, so a partial body would blank the other fields. A row scored
      // from entered games is the exception for its numbers: the game sheet
      // owns stats, raw and sport_score, so they stay out of the body (the
      // server keeps them too).
      const firstName = playerForm.first_name.trim();
      const lastName = playerForm.last_name.trim();
      const seasonStats = fromGames(selectedPlayer) ? {} : {
        sport_score: selectedPlayer.sport_score,
        stats: selectedPlayer.stats,
        raw: selectedPlayer.raw,
      };
      const body = {
        full_name: `${firstName} ${lastName}`.trim() || selectedPlayer.full_name,
        first_name: firstName,
        last_name: lastName,
        jersey_number: playerForm.jersey_number,
        position: playerForm.position.trim(),
        height: playerForm.height,
        weight: playerForm.weight,
        status: selectedPlayer.status,
        ...seasonStats,
        active: selectedPlayer.active,
        social_media: formToLinks(socialForm),
        irl_data: formToIrl(irlForm),
        profile_data: profileForm,
      };
      const res = await axios.put(
        `${ADMIN_URL}${selectedTeam.team_id}/players/${selectedPlayer.player_id}/`,
        body,
        authHeaders,
      );
      const updated = res.data.player;
      setPlayers((prev) => prev.map((p) => (p.player_id === updated.player_id ? updated : p)));
      toast.success(res.data.athlete_updated
        ? `Saved ${updated.full_name}'s data and updated their live profile.`
        : `Saved ${updated.full_name}'s data.`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    }
    setSaving(false);
  };

  // The player editor's heading: the edited name, or the roster name.
  const playerName = selectedPlayer
    ? (`${playerForm.first_name} ${playerForm.last_name}`.trim() || selectedPlayer.full_name)
    : '';
  // The open partner team's team plan (10/19 notes, D10, M-WAIVE 1.6): the
  // same block as under a code on the Team Codes tab.
  const planRequest = partnerPlanRequest(selectedTeam);

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        eyebrow="Admin Mode"
        title="Teams"
        subtitle={view === 'codes'
          ? 'One code per team. Athletes enter it in onboarding to join and verify, the same way they use a coach code.'
          : `${teams.length} CACHE Partner teams. They power CacheShare, onboarding and coach assignment.`}
        actions={(
          <>
            <Btn
              variant="secondary"
              onClick={() => setShowRequestsModal(true)}
              title="Teams athletes and coaches could not find in onboarding"
            >
              <Icon name="bell" size={14} className="mr-1.5" /> Requests
              {requests.length > 0 && <Badge tone="brand" className="ml-2">{requests.length}</Badge>}
            </Btn>
            <Btn
              variant="secondary"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploading}
              title="Bulk import teams and rosters from the Excel template. Partner Program, League, Sport, Gender, Level, Age Group, Season, City and State are read from the metadata block at the top of each sheet."
            >
              <Icon name="upload" size={14} className="mr-1.5" /> {uploading ? 'Uploading...' : 'Import .xlsx'}
            </Btn>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
            <Btn onClick={openCreateTeam}>
              <Icon name="plus" size={14} className="mr-1.5" /> New team
            </Btn>
          </>
        )}
      />

      <div className="mb-4">
        <SegmentedControl
          value={view}
          onChange={setView}
          options={[{ value: 'rosters', label: 'Partner rosters' }, { value: 'codes', label: 'Team codes' }]}
          capitalize={false}
        />
      </div>

      {view === 'codes' && <TeamCodesPanel authHeaders={authHeaders} />}

      {view === 'rosters' && (
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Team list */}
        <Card padding="p-0" className="w-full shrink-0 overflow-hidden lg:sticky lg:top-4 lg:w-[300px]">
          <div className="space-y-2 border-b border-bm-border p-3">
            <SearchBox value={teamSearch} onChange={setTeamSearch} placeholder="Search teams" aria-label="Search teams" />
            <div className="grid grid-cols-1 gap-2">
              <Select value={sport} onChange={(e) => { setSport(e.target.value); setGender(''); }} aria-label="Sport">
                <option value="">All sports</option>
                {sports.map((sp) => (
                  <option key={sp} value={sp}>{titleCase(sp)}</option>
                ))}
              </Select>
              <Select value={gender} onChange={(e) => setGender(e.target.value)} aria-label="Gender">
                <option value="">All genders</option>
                {genders.map((g) => (
                  <option key={g} value={g}>{GENDER_LABELS[g] || g}</option>
                ))}
              </Select>
              <Select
                value={onlyProvisional ? 'provisional' : ''}
                onChange={(e) => setOnlyProvisional(e.target.value === 'provisional')}
                aria-label="Review"
              >
                <option value="">All teams</option>
                <option value="provisional">{`Provisional only (${provisionalCount})`}</option>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-2 bm-body-s text-bm-muted">
              <span>{loadingTeams ? 'Loading teams...' : `${filteredTeams.length} teams`}</span>
              {filteredTeams.length > 100 && <span>Showing the first 100</span>}
            </div>
          </div>
          {loadingTeams ? (
            <Loading label="Loading teams..." />
          ) : filteredTeams.length === 0 ? (
            <EmptyState className="m-4">No teams match these filters.</EmptyState>
          ) : (
            <ul className="max-h-[calc(100vh-430px)] min-h-[240px] divide-y divide-bm-border overflow-y-auto">
              {filteredTeams.slice(0, 100).map((t) => {
                const isSelected = selectedTeam?.team_id === t.team_id;
                const meta = [
                  [t.city, t.state].filter(Boolean).join(', '),
                  t.partner_program || t.organization,
                  t.age_group,
                ].filter(Boolean).join(' · ');
                return (
                  <li key={t.team_id}>
                    <button
                      type="button"
                      onClick={() => selectTeam(t)}
                      className={`relative flex w-full flex-col gap-[2px] px-3 py-2.5 text-left transition-colors ${isSelected ? 'bg-bm-subtle' : 'hover:bg-bm-subtle'}`}
                    >
                      {isSelected && <span className="absolute bottom-0 left-0 top-0 w-[3px] bg-bm-orange" />}
                      <span className="flex min-w-0 items-center gap-2">
                        <span className={`truncate ${isSelected ? 'bm-label-l' : 'bm-body-m'} text-bm-ink`}>{t.name}</span>
                        {!t.active && <Badge tone="passive">Hidden</Badge>}
                        {t.provisional && <Badge tone="attention">Provisional</Badge>}
                      </span>
                      {meta && <span className="truncate bm-body-s text-bm-muted">{meta}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Selected team */}
        <div className="min-w-0 flex-1">
          {error && (
            <div className="mb-4">
              <ErrorState>{error}</ErrorState>
            </div>
          )}

          {!selectedTeam ? (
            <EmptyState className="min-h-[320px] justify-center">
              Select a team to manage its roster. Click a player to edit their data, or use the row actions to edit stats.
            </EmptyState>
          ) : (
            <Card padding="p-0" className="overflow-hidden">
              {/* Team header */}
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-bm-border px-5 py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="bm-h-s text-bm-ink">{selectedTeam.name}</h2>
                    {selectedTeam.active
                      ? <Badge tone="outline" dot={POSITIVE}>Active</Badge>
                      : <Badge tone="passive">Hidden</Badge>}
                    {selectedTeam.provisional && <Badge tone="attention">Provisional</Badge>}
                    <span className="bm-body-s text-bm-muted">{players.length} players</span>
                  </div>
                  {selectedTeam.provisional && (
                    <div className="mt-1 bm-body-s text-bm-muted">
                      Made when an athlete picked or added this team and CACHE had none for it. Confirm it, or merge it into the right team.
                    </div>
                  )}
                  <div className="mt-[2px] bm-body-s text-bm-muted">
                    {[selectedTeam.organization,
                      selectedTeam.age_group,
                      selectedTeam.gender ? GENDER_LABELS[selectedTeam.gender] : '',
                      selectedTeam.season]
                      .filter(Boolean).join(' · ')}
                    {selectedTeam.league_key && (
                      <span className="ml-2 font-mono text-bm-tertiary">{selectedTeam.league_key}</span>
                    )}
                  </div>
                  <div className="mt-2">
                    {selectedTeam.cerebro_program_id ? (
                      <Badge tone="soft" className="max-w-full !whitespace-normal !h-auto py-1">
                        <Icon name="activity" size={10} /> Merged with Cerebro: {programsOf(selectedTeam).map((p) => p.name || p.id).join(', ')}
                        {' · '}{players.filter((p) => p.cerebro_player_id).length} of {players.length} players linked
                      </Badge>
                    ) : (
                      <span className="bm-body-s text-bm-tertiary">Not merged with a Cerebro program. Edit the team to link one, or match players by name.</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {pendingRequesters > 0 && (
                    <Btn size="sm" onClick={() => setConfirmLinkAll(true)} disabled={teamBusy}
                      title="Link every pending request for this team to it, each as a single Link requester">
                      <Icon name="link" size={13} className="mr-1.5" /> {`Link all ${pendingRequesters} requester${pendingRequesters === 1 ? '' : 's'}`}
                    </Btn>
                  )}
                  {selectedTeam.provisional && (
                    <Btn variant="secondary" size="sm" onClick={() => confirmTeam(selectedTeam)} disabled={teamBusy}>
                      <Icon name="check" size={13} className="mr-1.5" /> Confirm team
                    </Btn>
                  )}
                  <Btn variant="secondary" size="sm" onClick={() => openEditTeam(selectedTeam)}>
                    <Icon name="edit" size={13} className="mr-1.5" /> Edit team
                  </Btn>
                  <Btn
                    variant="secondary"
                    size="sm"
                    onClick={matchRosterToCerebro}
                    disabled={matching}
                    title="Match every player to a Cerebro player and pull live stats"
                  >
                    <Icon name="refresh" size={13} className={`mr-1.5 ${matching ? 'animate-spin' : ''}`} /> {matching ? 'Matching...' : 'Match to Cerebro'}
                  </Btn>
                  <Btn
                    variant="secondary"
                    size="sm"
                    to={teamGamesPath(selectedTeam.team_id)}
                    title="Enter box scores for the games Cerebro does not capture"
                  >
                    <Icon name="calendar" size={13} className="mr-1.5" /> Game stats
                  </Btn>
                  <Btn size="sm" onClick={openCreatePlayer}>
                    <Icon name="plus" size={13} className="mr-1.5" /> Add player
                  </Btn>
                  <button
                    type="button"
                    onClick={() => setPendingDelete({ kind: 'team', team: selectedTeam })}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
                    title="Delete team"
                    aria-label="Delete team"
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </div>
              </div>

              {/* Team plan: Premium for every vouched player of this team, or
                  of its whole program (TeamCodesPanel.TeamPlanBlock). */}
              {planRequest && (
                <div className="border-b border-bm-border px-5 py-4">
                  <TeamPlanBlock request={planRequest} authHeaders={authHeaders} program={programOf(null)} code={null} />
                </div>
              )}

              {loadingRoster ? (
                <Loading label="Loading roster..." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-bm-subtle">
                      <tr>
                        {['#', 'Name', 'Position', 'GP', 'PTS', 'CACHE Score', 'Socials', 'IRL', 'Claimed', 'Cerebro'].map((h) => (
                          <th key={h} className="whitespace-nowrap border-b border-bm-border px-3 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">{h}</th>
                        ))}
                        <th className="whitespace-nowrap border-b border-bm-border px-3 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((p) => (
                        <tr
                          key={p.player_id}
                          onClick={() => pickPlayer(p)}
                          className="cursor-pointer border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle"
                        >
                          <td className="px-3 py-2.5 bm-body-m text-bm-muted">{p.jersey_number || '-'}</td>
                          <td className="px-3 py-2.5 bm-label-l text-bm-ink">
                            {p.athlete_email && p.athlete_profile?.athleteId ? (
                              <Link
                                to={`/business/athlette/profile/${p.athlete_profile.athleteId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-2 transition-colors hover:text-bm-orange"
                                title="Open athlete profile in a new tab"
                              >
                                <Avatar
                                  src={p.athlete_profile.avatar}
                                  initials={initialsOf(p.full_name)}
                                  size={28}
                                  tone="brand"
                                />
                                <span className="underline-offset-2 hover:underline">{p.full_name}</span>
                              </Link>
                            ) : (
                              p.full_name
                            )}
                          </td>
                          <td className="px-3 py-2.5 bm-body-m text-bm-muted">{p.position || '-'}</td>
                          <td className="px-3 py-2.5 bm-body-m text-bm-muted">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              {liveLeads(p) ? p.live_stats.games_played : (p.raw?.GP ?? '-')}
                              {fromGames(p) && (
                                <span title={gamesSourceTitle(p)}>
                                  <Badge tone="soft"><Icon name="calendar" size={10} /> Games</Badge>
                                </span>
                              )}
                            </div>
                            {enteredGamesNote(p) && (
                              <div className="whitespace-nowrap bm-body-s text-bm-tertiary">{enteredGamesNote(p)}</div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 bm-body-m text-bm-muted">
                            {liveLeads(p) ? p.live_stats.total_pts : (p.raw?.PTS ?? '-')}
                          </td>
                          <td className="px-3 py-2.5">
                            {/* CacheScore: the stored score for a claimed athlete, otherwise
                                the calculator's blend of the sport score (live Cerebro when
                                linked) with the visibility and IRL pillars. */}
                            <div
                              className="bm-label-l text-bm-orange"
                              title={p.cache_score_source === 'stored'
                                ? 'Stored CacheScore from the claimed athlete profile'
                                : 'CacheScore blended from the sport score (no stored score yet)'}
                            >
                              {p.cache_score != null ? p.cache_score : '0'}
                              {p.cache_score_source === 'stored' && (
                                <span className="ml-0.5 align-super text-[9px] font-bold text-bm-ink">CS</span>
                              )}
                            </div>
                            <div className="whitespace-nowrap bm-body-s text-bm-tertiary">
                              Sport {Number(p.sport_score_effective ?? p.sport_score ?? 0).toFixed(1)}
                              {p.score_source === 'cerebro' && (
                                <span className="ml-1 text-[9px] font-bold text-bm-orange" title={`Live Cerebro score from ${p.live_stats?.team || 'their latest team'}`}>LIVE</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            {rowSocials(p).length > 0 ? (
                              <Badge tone="soft"><Icon name="link" size={10} /> {rowSocials(p).length}</Badge>
                            ) : (
                              <span className="bm-body-s text-bm-tertiary">none</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {rowIrlCount(p) > 0 ? (
                              <Badge tone="outline"><Icon name="award" size={10} /> {rowIrlCount(p)}</Badge>
                            ) : (
                              <span className="bm-body-s text-bm-tertiary">none</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {p.athlete_email ? (
                              <Badge tone="outline" dot={POSITIVE}>Claimed</Badge>
                            ) : (
                              <span className="bm-body-s text-bm-tertiary">Unclaimed</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {p.cerebro_player_id ? (
                              <span title={`Cerebro: ${p.cerebro_match?.name || p.cerebro_player_id}${p.live_stats?.team ? ` · stats via ${p.live_stats.team}` : ''}`}>
                                <Badge tone="soft" className="max-w-[220px]">
                                  <Icon name="activity" size={10} /> <span className="truncate">{p.live_stats?.team || p.cerebro_match?.name || 'Linked'}</span>
                                </Badge>
                              </span>
                            ) : matchInfo[p.player_id]?.candidates?.length ? (
                              <Badge tone="attention">{matchInfo[p.player_id].candidates.length} to review</Badge>
                            ) : (
                              <span className="bm-body-s text-bm-tertiary">Not on Cerebro</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div
                              className="flex items-center justify-end gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {fromGames(p) ? (
                                // The game sheet owns this row's numbers. The
                                // span carries the tooltip, which a disabled
                                // button does not show in every browser.
                                <span title="Stats come from entered games" className="inline-flex">
                                  <button
                                    type="button"
                                    disabled
                                    className="inline-flex h-8 w-8 cursor-not-allowed items-center justify-center rounded-full text-bm-tertiary"
                                    aria-label={`Stats for ${p.full_name} come from entered games`}
                                  >
                                    <Icon name="lock" size={14} />
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openEditStats(p)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
                                  title="Edit season stats"
                                  aria-label={`Edit season stats for ${p.full_name}`}
                                >
                                  <Icon name="trending-up" size={14} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setPendingDelete({ kind: 'player', player: p })}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-mutedbg hover:text-bm-ink"
                                title="Remove player"
                                aria-label={`Remove ${p.full_name}`}
                              >
                                <Icon name="trash" size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {players.length === 0 && (
                        <tr>
                          <td colSpan={11} className="px-3 py-10 text-center bm-body-m text-bm-muted">
                            No players yet. Add players or import via Excel.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
      )}

      {/* Team requests: find in the API and link, create a partner team, or dismiss */}
      {showRequestsModal && (
        <TeamRequestsModal
          requests={requests}
          authHeaders={authHeaders}
          onClose={() => setShowRequestsModal(false)}
          onCreatePartner={adoptRequest}
          onResolved={fetchRequests}
        />
      )}

      {/* Team create/edit modal */}
      {showTeamModal && !dupes && (
        <TeamModal
          form={teamForm}
          setForm={setTeamForm}
          editing={!!editingTeamId}
          fromRequest={!editingTeamId && !!adoptingRequestId}
          onSubmit={saveTeam}
          onClose={closeTeamModal}
        />
      )}

      {dupes && (
        <DuplicateTeamsModal
          dupes={dupes}
          fromRequest={!!adoptingRequestId}
          onBack={() => setDupes(null)}
          onCreateAnyway={() => createTeam({ ...dupes.payload, confirmedNoMatch: true })}
          onOpenTeam={(row) => {
            const team = teams.find((t) => t.team_id === (row.cacheTeamId || row.teamId));
            closeTeamModal();
            if (team) selectTeam(team);
          }}
          onOpenRequests={() => { closeTeamModal(); setShowRequestsModal(true); }}
          onLinkRequester={linkRequestToExisting}
        />
      )}

      {confirmLinkAll && selectedTeam && (
        <ConfirmModal
          title="Link all requesters"
          body={`Link the ${pendingRequesters} pending request${pendingRequesters === 1 ? '' : 's'} for ${selectedTeam.name} to this team? Each one is linked as a single Link requester: with no team yet, it becomes their team; with a team already, it is added as another team. A requester the team does not fit stays pending.`}
          confirmLabel={`Link all ${pendingRequesters}`}
          cancelLabel="Cancel"
          requireAck={false}
          busy={teamBusy}
          onConfirm={linkAllRequesters}
          onClose={() => !teamBusy && setConfirmLinkAll(false)}
        />
      )}

      {/* Season stats / new player modal */}
      {showStatsModal && (
        <StatsModal
          form={statsForm}
          setForm={setStatsForm}
          editing={!!editingPlayerId}
          onSubmit={saveStats}
          onClose={() => { setShowStatsModal(false); setEditingPlayerId(null); }}
        />
      )}

      {/* Player data editor: the profile-style entry panel, on BmModal.
          Not dismissible: Escape or a stray backdrop click would drop the
          unsaved edits, so only Cancel and the X close it. The title sits in
          the modal's h2, so it is built from spans. */}
      {showModal && selectedPlayer && (
        <BmModal
          width={1100}
          onClose={closeModal}
          dismissible={false}
          title={(
            <span className="flex min-w-0 items-center gap-4">
              <Avatar
                src={selectedPlayer.athlete_profile?.avatar}
                initials={initialsOf(playerName)}
                size={56}
                tone="brand"
              />
              <span className="block min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate bm-h-m text-bm-ink">{playerName}</span>
                  {playerForm.jersey_number && (
                    <span className="bm-h-s text-bm-tertiary">#{playerForm.jersey_number}</span>
                  )}
                  {selectedPlayer.cache_score != null && (
                    <Badge tone="soft">
                      CACHE {selectedPlayer.cache_score}
                      <span className="text-bm-muted">· sport {Number(selectedPlayer.sport_score_effective ?? 0).toFixed(1)}</span>
                    </Badge>
                  )}
                  {selectedPlayer.athlete_email
                    ? <Badge tone="outline" dot={POSITIVE}>Claimed</Badge>
                    : <Badge tone="passive">Unclaimed</Badge>}
                </span>
                <span className="mt-0.5 block truncate bm-body-m text-bm-muted">
                  {[playerForm.position, selectedTeam?.name].filter(Boolean).join(' · ')}
                </span>
              </span>
            </span>
          )}
          footer={confirmOpen ? (
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="bm-body-m text-bm-muted">
                <span className="bm-label-l text-bm-ink">Save {playerName}'s data?</span>{' '}
                {selectedPlayer.athlete_email
                  ? 'This updates the roster record and the athlete\'s live profile.'
                  : 'This updates the roster record and will prefill the profile at claim.'}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Btn variant="secondary" onClick={() => setConfirmOpen(false)} disabled={saving}>Back</Btn>
                <Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Yes, save'}</Btn>
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center justify-end gap-2">
              <Btn variant="secondary" onClick={closeModal}>Cancel</Btn>
              <Btn onClick={() => setConfirmOpen(true)}>Save player data</Btn>
            </div>
          )}
        >
          {/* Cerebro player link: live stats come from wherever this
              player last played (school, travel team, showcase). */}
          <div className="mb-4 rounded-[12px] border border-bm-border bg-bm-subtle px-4 py-3 bm-body-m text-bm-muted">
            {selectedPlayer.cerebro_player_id ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 bm-label-l text-bm-n700">
                    <Icon name="activity" size={13} /> Linked to Cerebro player {selectedPlayer.cerebro_match?.name || selectedPlayer.cerebro_player_id}
                  </div>
                  {selectedPlayer.live_stats?.games_played > 0 ? (
                    <div className="mt-0.5 bm-body-s text-bm-muted">
                      Live score {Number(selectedPlayer.live_stats.sport_score || 0).toFixed(1)} · {selectedPlayer.live_stats.games_played} GP · {selectedPlayer.live_stats.pts_per_game} PPG
                      {selectedPlayer.live_stats.team ? ` · via ${selectedPlayer.live_stats.team}` : ''}
                      {selectedPlayer.live_stats.competition ? ` (${selectedPlayer.live_stats.competition})` : ''}
                    </div>
                  ) : (
                    <div className="mt-0.5 bm-body-s text-bm-muted">
                      {fromGames(selectedPlayer)
                        ? 'No games on Cerebro yet, so the stats from entered games are used.'
                        : 'No games on Cerebro yet, so the roster stats below are used.'}
                    </div>
                  )}
                </div>
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={() => linkCerebroPlayer(selectedPlayer, '', '')}
                  disabled={cerebroLinking}
                  className="shrink-0"
                >
                  {cerebroLinking ? 'Working...' : 'Unlink from Cerebro'}
                </Btn>
              </div>
            ) : (
              <div>
                <div className="bm-label-l text-bm-ink">
                  {fromGames(selectedPlayer)
                    ? 'Not linked to a Cerebro player. Stats come from entered games.'
                    : 'Not linked to a Cerebro player. Stats come from the roster record.'}
                </div>
                {(matchInfo[selectedPlayer.player_id]?.candidates || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {matchInfo[selectedPlayer.player_id].candidates.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-2 rounded-[8px] border border-bm-border bg-white px-3 py-1.5 bm-body-s">
                        <span className="truncate">
                          <span className="bm-label-m text-bm-ink">{c.name}</span>
                          {c.program ? ` · ${c.program}` : ''}{c.competition ? ` (${c.competition})` : ''}{c.latest ? ` · ${c.latest}` : ''}{c.hs ? ` · ${c.hs}` : ''}
                        </span>
                        <Btn
                          size="sm"
                          onClick={() => linkCerebroPlayer(selectedPlayer, c.id, c.name)}
                          disabled={cerebroLinking}
                          aria-label={`Link ${c.name}`}
                          className="shrink-0"
                        >
                          Link
                        </Btn>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="relative mt-2">
                  <Input
                    value={cerebroQuery}
                    onChange={(e) => setCerebroQuery(e.target.value)}
                    placeholder="Search Cerebro players by name"
                  />
                  {cerebroQuery.trim().length >= 2 && (cerebroSearching || cerebroHits.length > 0) && (
                    <ul className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-[8px] border border-bm-border bg-white bm-body-s shadow-xl">
                      {cerebroSearching && cerebroHits.length === 0 && <li className="px-3 py-2 text-bm-tertiary">Searching Cerebro...</li>}
                      {cerebroHits.map((h) => (
                        <li key={h.cerebroId} className="flex items-center justify-between gap-2 border-b border-bm-border px-3 py-1.5 last:border-0">
                          <span className="truncate">
                            <span className="bm-label-m text-bm-ink">{h.name}</span>
                            {h.teamName ? ` · ${h.teamName}` : ''}
                          </span>
                          <Btn
                            size="sm"
                            onClick={() => linkCerebroPlayer(selectedPlayer, h.cerebroId, h.name)}
                            disabled={cerebroLinking}
                            aria-label={`Link ${h.name}`}
                            className="shrink-0"
                          >
                            Link
                          </Btn>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
          {selectedPlayer.athlete_email ? (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-bm-border bg-bm-subtle px-4 py-2.5 bm-body-m text-bm-muted">
              <span>
                Connected to {selectedPlayer.athlete_email}. This form is
                prefilled with the athlete's live profile data, and saving
                updates both the roster record and their profile.
              </span>
              <Btn
                size="sm"
                variant="secondary"
                onClick={() => linkAthlete(true)}
                disabled={linking}
                className="shrink-0"
              >
                {linking ? 'Working...' : 'Unlink'}
              </Btn>
            </div>
          ) : (
            <div className="mb-6 rounded-[12px] border border-bm-border bg-bm-subtle px-4 py-2.5 bm-body-m text-bm-muted">
              <div>
                Unclaimed. This data will prefill the athlete's profile when they
                sign up and claim this roster spot.
              </div>
              {/* Athletes can be on several rosters (school + travel team):
                  link this row to an existing account directly. */}
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="bm-label-m text-bm-ink">
                  Already on CACHE? Link their account:
                </span>
                <Input
                  type="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); linkAthlete(false); } }}
                  placeholder="athlete@email.com"
                  aria-label="Athlete account email"
                  className="min-w-[220px] flex-1 !w-auto"
                />
                <Btn
                  size="sm"
                  onClick={() => linkAthlete(false)}
                  disabled={linking || !linkEmail.trim()}
                >
                  {linking ? 'Linking...' : 'Link account'}
                </Btn>
              </div>
            </div>
          )}

          {/* Player info: the roster row's own fields */}
          <div className="mb-8">
            <SectionHeader icon="users">Player info</SectionHeader>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Field label="First name">
                <Input type="text" value={playerForm.first_name} onChange={(e) => setPlayerForm({ ...playerForm, first_name: e.target.value })} />
              </Field>
              <Field label="Last name">
                <Input type="text" value={playerForm.last_name} onChange={(e) => setPlayerForm({ ...playerForm, last_name: e.target.value })} />
              </Field>
              <Field label="Jersey #">
                <Input type="text" value={playerForm.jersey_number} onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })} placeholder="e.g. 23" />
              </Field>
              <Field label="Position">
                <Input type="text" value={playerForm.position} onChange={(e) => setPlayerForm({ ...playerForm, position: e.target.value })} placeholder="e.g. G, F, QB" />
              </Field>
              <Field label="Height (in)">
                <Input type="text" value={playerForm.height} onChange={(e) => setPlayerForm({ ...playerForm, height: e.target.value })} placeholder="e.g. 74" />
              </Field>
              <Field label="Weight (lb)">
                <Input type="text" value={playerForm.weight} onChange={(e) => setPlayerForm({ ...playerForm, weight: e.target.value })} placeholder="e.g. 180" />
              </Field>
            </div>
          </div>

          {/* Profile details staged for the athlete's profile at claim */}
          <div className="mb-8">
            <SectionHeader icon="user">Profile details</SectionHeader>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Field label="Grad year">
                <Input type="number" min="1900" max="2100" value={profileForm.gradYear} onChange={(e) => setProfileForm({ ...profileForm, gradYear: e.target.value })} placeholder="e.g. 2027" />
              </Field>
              <Field label="Hometown">
                <Input type="text" value={profileForm.homeTown} onChange={(e) => setProfileForm({ ...profileForm, homeTown: e.target.value })} />
              </Field>
              <Field label="State">
                <Input type="text" value={profileForm.state} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })} placeholder="e.g. CA" />
              </Field>
              <Field label="Date of birth">
                <Input type="date" value={profileForm.dateOfBirth} onChange={(e) => setProfileForm({ ...profileForm, dateOfBirth: e.target.value })} />
              </Field>
              <Field label="Coach name">
                <Input type="text" value={profileForm.coachName} onChange={(e) => setProfileForm({ ...profileForm, coachName: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
            {/* Socials column */}
            <div>
              <SectionHeader icon="link">Social media links</SectionHeader>
              <div className="space-y-3">
                {PLATFORMS.map((p) => (
                  <Field key={p.key} label={p.label}>
                    <Input
                      type="url"
                      value={socialForm[p.key]}
                      onChange={(e) => setSocialForm({ ...socialForm, [p.key]: e.target.value })}
                      placeholder={p.placeholder}
                    />
                  </Field>
                ))}
                <Field label="Other links">
                  <TextArea
                    value={socialForm.other}
                    onChange={(e) => setSocialForm({ ...socialForm, other: e.target.value })}
                    rows={2}
                    placeholder="One URL per line"
                  />
                </Field>
              </div>
            </div>

            {/* IRL column */}
            <div>
              <SectionHeader icon="award">IRL impact</SectionHeader>
              <div className="space-y-4">
                <Field label="GPA">
                  <Input
                    type="number"
                    min="0"
                    max="6"
                    step="0.1"
                    value={irlForm.gpa}
                    onChange={(e) => setIrlForm({ ...irlForm, gpa: e.target.value })}
                    placeholder="e.g. 3.8"
                  />
                </Field>
                <ItemListEditor
                  label="Academic achievements"
                  items={irlForm.academicAwards}
                  onChange={(items) => setIrlForm({ ...irlForm, academicAwards: items })}
                  levels={IRL_LEVELS}
                />
                <ItemListEditor
                  label="Athletic achievements / honors"
                  items={irlForm.athleticAwards}
                  onChange={(items) => setIrlForm({ ...irlForm, athleticAwards: items })}
                  levels={AWARD_LEVELS}
                />
                <ItemListEditor
                  label="Community service / activities"
                  items={irlForm.activities}
                  onChange={(items) => setIrlForm({ ...irlForm, activities: items })}
                  levels={IRL_LEVELS}
                />
                <Field label="Enrichment programs">
                  <TextArea
                    value={irlForm.enrichments}
                    onChange={(e) => setIrlForm({ ...irlForm, enrichments: e.target.value })}
                    rows={2}
                    placeholder="One program per line, e.g. Honor Roll, Coding Camp"
                  />
                </Field>
                <InternshipsEditor
                  items={irlForm.internships}
                  onChange={(items) => setIrlForm({ ...irlForm, internships: items })}
                />
                <NilDealsEditor
                  items={irlForm.nilDeals}
                  onChange={(items) => setIrlForm({ ...irlForm, nilDeals: items })}
                />
              </div>
            </div>
          </div>
        </BmModal>
      )}

      {/* Delete team / remove player confirmation */}
      {pendingDelete && (
        <ConfirmModal
          tone="danger"
          busy={deleting}
          title={pendingDelete.kind === 'team' ? 'Delete team' : 'Remove player'}
          body={pendingDelete.kind === 'team'
            ? `Delete ${pendingDelete.team.name}? All players on this team will be removed.`
            : `Remove ${pendingDelete.player.full_name} from ${selectedTeam?.name || 'this team'}?`}
          confirmLabel={pendingDelete.kind === 'team' ? 'Delete team' : 'Remove player'}
          onConfirm={runPendingDelete}
          onClose={() => !deleting && setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default TeamManagementPage;
